<template>
  <cv-accordion class="accordion-wrapper" :class="cssClasses" @change="handleChange">
    <slot />
  </cv-accordion>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { AccordionActionData } from "@plugins/common/types.ts";

// Props
interface Props {
  kind?: string;
  checkbox?: boolean;
}

const { kind = "", checkbox = false } = defineProps<Props>();

const emit = defineEmits<(e: "stateChange", payload: AccordionActionData) => void>();

const validateKind = (value: string): boolean => {
  return ["", "gray", "ghost"].includes(value);
};

const cssClasses = computed(() => {
  const classes: string[] = [];

  if (!validateKind(kind)) {
    classes.push("accordion-default");
  } else {
    classes.push(kind === "" ? "accordion-default" : `accordion-${kind}`);
  }

  if (checkbox) {
    classes.push("accordion-checkbox");
  }

  return classes.join(" ");
});

const handleChange = (data: {
  state: {
    id: string;
    open: boolean;
  }[];
  change: { id: string };
}) => {
  const state = data.state.map((item) => item.open);
  const index = data.state.findIndex((item) => item.id === data.change?.id);

  emit("stateChange", {
    changedIndex: index,
    state: state,
  });
};
</script>

<style lang="scss">
@import "@styles/abstracts/mixins";
@import "@styles/themes/sls-theme/tokens/colors";
@import "@styles/themes/sls-theme/tokens/spacing";
@import "@styles/themes/sls-theme/tokens/typo";

/***
Styles for the accordion component:
1. Default, accordion-default
2. Gray, accordion-gray
3. Ghost, accordion-ghost
4. Checkbox, accordion-checkbox
*/

.bx--accordion {
  .bx--accordion__item {
    margin-bottom: $spacing-03;
    border-radius: 10px;
    border: none;
    background-color: $ui-01;

    .bx--side-nav__items & {
      border-bottom: none;
    }

    .bx--content-switcher-btn {
      background-color: $ui-background;
    }

    .bx--content-switcher--selected {
      background-color: $ui-05;
    }
  }

  .bx--accordion__content {
    padding-right: $spacing-05;
  }

  &.accordion-gray {
    .bx--accordion__item {
      background-color: $ui-background;
      border: 1px solid transparent;

      &.bx--accordion__item--active {
        background-color: $ui-01;
        border: 1px solid $ui-03;
      }
    }
  }

  &.accordion-ghost {
    .bx--accordion__item {
      background: none;

      &:last-child {
        border-bottom: 0;
      }

      .bx--accordion__title {
        @include carbon--type-style("productive-heading-01");
        display: inline-block;
        color: $interactive-01;
        text-transform: uppercase;
        padding-right: 0;
        margin: 0;

        &::after {
          position: relative;
          top: -2px;
          left: $spacing-05;
        }
      }

      &--active {
        > .bx--accordion__heading {
          background: none;

          .bx--accordion__title {
            &::after {
              top: 3px;
              border-color: $interactive-01;
            }
          }
        }

        .bx--accordion__content {
          padding: 0;
        }
      }
    }
  }

  &.accordion-checkbox {
    position: relative;

    > .input-checkbox {
      @include position-mixin($top: 0.7rem, $left: 1rem);
      margin: 0;
    }

    .bx--accordion__heading {
      align-items: center;
      min-height: 3rem;
      padding: $spacing-04 0 $spacing-04 $spacing-09;

      .bx--accordion__title {
        margin-left: $spacing-02;
      }
    }
  }
}

.bx--accordion__heading {
  border-radius: 10px;

  &:hover,
  &:focus {
    &:before {
      display: none;
    }
  }

  .sls-icon {
    &:not([name*="Disabled"]) {
      [stroke] {
        stroke: $interactive-01;
      }
    }
  }

  .bx--accordion__arrow {
    display: none;
  }

  .bx--accordion__title {
    position: relative;
    padding-right: $spacing-10;

    &:after {
      content: "";
      position: absolute;
      display: inline-block;
      top: 4px;
      right: 1rem;
      width: 11px;
      height: 11px;
      border-bottom: 1.5px solid $icon-01;
      border-right: 1.5px solid $icon-01;
      transform: rotate(45deg);
    }

    .title {
      margin-bottom: 0;
      @include carbon--type-style("body-short-01", true);
    }

    .sls-icon {
      &[name*="Trash"] {
        [fill] {
          fill: $interactive-01;
        }
      }
    }
  }
}

.bx--accordion__item--active {
  border-color: $ui-06;

  > .bx--accordion__heading {
    background-color: $ui-05;
    color: $ui-01;
    border-radius: 10px 10px 0 0;

    &:before {
      display: none;
    }

    &:hover {
      background-color: $hover-secondary;
    }

    .sls-icon {
      [stroke] {
        stroke: $inverse-link;
      }
    }

    .bx--accordion__title {
      &:after {
        top: 8px;
        border-color: $inverse-link;
        transform: rotate(225deg);

        .hint & {
          border-color: $interactive-01;
        }
      }

      .sls-icon {
        &[name*="Trash"] {
          [fill] {
            fill: $inverse-link;
          }
        }
      }
    }
  }

  .bx--accordion__content {
    border-radius: 0 0 10px 10px;
    padding-bottom: $spacing-03;
  }

  // Styling for nested accordion item as Carbon doesn't have the styling for it
  // https://github.com/carbon-design-system/carbon/issues/4768
  .bx--accordion__item {
    .bx--accordion__content {
      display: none;
    }

    &.bx--accordion__item--active {
      .bx--accordion__content {
        display: block;
      }
    }
  }
}
</style>
